import { API_BASE_URL } from "../../constant/constant";
import React, { useState, useEffect } from "react";
import { useOutletContext, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import PricingCard from "../../Components/Cards/PricingCard";
import {
  BillingSection,
  CancelButton,
  CardText,
  MainContainer,
  SubText,
  Subheader,
  SubscriptionCard,
  Title,
  CardInfo,
  RowDiv,
  RowText,
  DownloadButton,
  BillingButton,
  PlansDiv,
  BillingButton2,
} from "./style";
import CircularProgress from "@mui/material/CircularProgress";
import SnackbarAlert from "../../Components/Alerts/SnackbarAlert";
import { CardImgs } from "../../Components/Constants/Constants";
import AddCardWrapper from "../../Components/modals/AddCardModal";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Button from "@mui/material/Button";

const Billing = () => {
  const { t } = useTranslation();
  const { isSidebarOpen } = useOutletContext();
  const [loading, setLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [planName, setPlanName] = useState("");
  const [card, setCard] = useState([]);
  const [cardBrandImg, setCardBrandImg] = useState("");
  const [cardExpiryYear, setCardExpiryYear] = useState("");
  const [cardBrand, setCardBrand] = useState("");
  const [nextInvoiceDate, setNextInvoiceDate] = useState("");
  const [periodEnd, setPeriodEnd] = useState(""); // State for period end date
  const [invoices, setInvoices] = useState([]);
  const [subsCancelsAt, setSubsCancelsAt] = useState(null);
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [showCardModal, setShowCardModal] = useState(false);
  const [isTrialPeriod, setIsTrialPeriod] = useState(false);
  const [trialExpiryDate, setTrialExpiryDate] = useState("");
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [currentPlanTier, setCurrentPlanTier] = useState(""); // essential, advanced, or business
  const [currentPlanMode, setCurrentPlanMode] = useState(""); // monthly or yearly
  const [showUpgradeCards, setShowUpgradeCards] = useState(false);
  const [showDowngradeCards, setShowDowngradeCards] = useState(false);
  const [upgradeMode, setUpgradeMode] = useState("monthly"); // For upgrade toggle
  const [downgradeMode, setDowngradeMode] = useState("monthly"); // For downgrade toggle
  const navigate = useNavigate();

  const fetchCardDetails = async () => {
    const email = localStorage.getItem("email");
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/auth/get-card?email=${email}`,
      );
      const data = await response.json();

      if (data.responseCode === 2000) {
        setCard(data.response);
        setCardBrand(data.response.cardBrand.toLowerCase());
        setCardBrandImg(CardImgs[data.response.cardBrand.toLowerCase()]);
        setCardExpiryYear(data.response.cardExpiryYear);
      } else {
        console.error("Failed to retrieve card details:", data.message);
      }
    } catch (error) {
      console.error("Error fetching card details:", error);
    }
  };

  useEffect(() => {
    // Check if billing information exists
    const packageID = localStorage.getItem("packageID");
    const nextInvoice = localStorage.getItem("nextInvoice");
    const cancelsAt = localStorage.getItem("subsCancelsAt");
    const trialExpiry = localStorage.getItem("trialExpiryDate");

    if (!packageID && !nextInvoice && !cancelsAt && trialExpiry) {
      const trialDate = new Date(trialExpiry);
      const today = new Date();

      if (today < trialDate) {
        setIsTrialPeriod(true);
        const day = trialDate.getDate();
        const month = trialDate.toLocaleString("default", { month: "short" });
        const year = trialDate.getFullYear();
        setTrialExpiryDate(`${day} ${month}, ${year}`);
        setPlanName("Free Trial");
        setIsLoadingData(false);
        return; // Exit early as we don't need to fetch other data
      }
    }

    // Parse packageID to determine plan tier and mode
    if (packageID) {
      const [tier, mode] = packageID.split("_");
      setCurrentPlanTier(tier); // essential, advanced, or business
      setCurrentPlanMode(mode); // monthly or yearly

      // Set plan name based on tier and mode
      if (tier === "essential") {
        setPlanName(mode === "monthly" ? "Essential Monthly" : "Essential Yearly");
      } else if (tier === "advanced") {
        setPlanName(mode === "monthly" ? "Advanced Monthly" : "Advanced Yearly");
      } else if (tier === "business") {
        setPlanName(mode === "monthly" ? "Business Monthly" : "Business Yearly");
      }
    } else {
      // Legacy support for old packageID format
      if (packageID === "1") {
        setPlanName("Monthly Plan");
      } else if (packageID === "2") {
        setPlanName("Yearly Plan");
      } else {
        setPlanName("Pro Annual");
      }
    }

    if (nextInvoice) {
      const date = new Date(nextInvoice);
      const day = date.getDate();
      const month = date.toLocaleString("default", { month: "short" });
      const year = date.getFullYear();
      setNextInvoiceDate(`${day} ${month}, ${year}`);
    }

    if (cancelsAt) {
      const cancelDate = new Date(cancelsAt);
      const day = cancelDate.getDate();
      const month = cancelDate.toLocaleString("default", { month: "short" });
      const year = cancelDate.getFullYear();
      setPeriodEnd(`${day} ${month}, ${year}`);
      setSubsCancelsAt(cancelsAt);
    }

    const fetchInvoices = async () => {
      const email = localStorage.getItem("email");
      try {
        const response = await fetch(
          `${API_BASE_URL}/api/auth/getUserInvoices?email=${email}`,
        );
        const data = await response.json();
        console.log("invoice response", data)
        if (data.responseCode === 2000) {
          setInvoices(data.response);
        } else {
          console.error("Failed to fetch invoices:", data.message);
        }
      } catch (error) {
        console.error("Error fetching invoices:", error);
      } finally {
        setIsLoadingData(false); // Stop loading once data is fetched
      }
    };
    fetchCardDetails();
    fetchInvoices();
  }, []);

  const handleCancelSubscription = async () => {
    setLoading(true); // Start loading
    const stripeCustomerId = localStorage.getItem("stripeCustomerId");
    if (!stripeCustomerId) {
      console.error("Stripe Customer ID not found");
      setSnackbarMessage(t("billing.messages.customerNotFound"));
      setSnackbarOpen(true);
      setLoading(false); // Stop loading
      return;
    }

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/auth/cancel-subscription`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ stripeCustomerId }),
        },
      );
      const data = await response.json();

      if (data.responseCode === 2000) {
        if (data.response.status === "active") {
          console.log("Subscription canceled successfully");
          setSnackbarMessage(t("billing.messages.cancelSuccess"));
          setSnackbarOpen(true);
          // Set periodEnd with the cancel_at date from the response
          const cancelDate = new Date(data.response.cancel_at * 1000);
          const day = cancelDate.getDate();
          const month = cancelDate.toLocaleString("default", {
            month: "short",
          });
          const year = cancelDate.getFullYear();
          setPeriodEnd(`${day} ${month}, ${year}`);
          localStorage.setItem("subsCancelsAt", data.response.cancel_at * 1000);
          setSubsCancelsAt(data.response.cancel_at * 1000);
        } else if (data.response.status === "canceled") {
          localStorage.clear();
          navigate("/login");
        }
      } else {
        console.error("Failed to cancel subscription:", data.message);
        setSnackbarMessage(t("billing.messages.cancelError"));
        setSnackbarOpen(true);
      }
    } catch (error) {
      console.error("Error canceling subscription:", error);
      setSnackbarMessage(t("billing.messages.cancelError"));
      setSnackbarOpen(true);
    } finally {
      setLoading(false); // Stop loading
    }
  };

  const handleDownloadInvoice = (url) => {
    window.open(url, "_blank");
  };

  const handleChangeCard = () => {
    navigate("/change-card");
  };

  const handleshowCardModal = () => {
    setShowCardModal(true);
  };

  const handleHideCardModal = () => {
    setShowCardModal(false);
  };

  const handleUpgradePlan = () => {
    // Check current plan tier and determine what to show
    if (currentPlanTier === "business") {
      // Already on highest tier
      setSnackbarMessage(t("billing.messages.highestPlan"));
      setSnackbarOpen(true);
    } else {
      // Set initial upgrade mode to current mode
      setUpgradeMode(currentPlanMode);
      // Show upgrade cards
      setShowUpgradeCards(true);
    }
  };

  const handleCloseUpgradeCards = () => {
    setShowUpgradeCards(false);
  };

  const handleUpgradeSuccess = () => {
    setShowUpgradeCards(false);
    setSnackbarMessage(t("billing.messages.upgradeSuccess"));
    setSnackbarOpen(true);

    // Redirect to billing page after a short delay to show the snackbar
    setTimeout(() => {
      window.location.href = "/billing";
    }, 1500);
  };

  const handleDowngradePlan = () => {
    // Check current plan tier and determine what to show
    if (currentPlanTier === "essential") {
      // Already on lowest tier
      setSnackbarMessage(t("billing.messages.lowestPlan"));
      setSnackbarOpen(true);
    } else {
      // Set downgrade mode to current mode (can't change mode during downgrade)
      setDowngradeMode(currentPlanMode);
      // Show downgrade cards
      setShowDowngradeCards(true);
    }
  };

  const handleCloseDowngradeCards = () => {
    setShowDowngradeCards(false);
  };

  const handleDowngradeSuccess = () => {
    setShowDowngradeCards(false);
    setSnackbarMessage(t("billing.messages.downgradeSuccess"));
    setSnackbarOpen(true);

    // Redirect to billing page after a short delay to show the snackbar
    setTimeout(() => {
      window.location.href = "/billing";
    }, 1500);
  };

  const handleDowngradeModeToggle = () => {
    setDowngradeMode(downgradeMode === "monthly" ? "yearly" : "monthly");
  };

  const handleUpgradeModeToggle = () => {
    setUpgradeMode(upgradeMode === "monthly" ? "yearly" : "monthly");
  };

  const handleCancelClick = () => {
    setOpenConfirmDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenConfirmDialog(false);
  };

  const handleConfirmCancel = () => {
    setOpenConfirmDialog(false);
    handleCancelSubscription();
  };

  return (
    <>
      <MainContainer isOpen={isSidebarOpen}>
        {isLoadingData ? (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100vh",
            }}
          >
            <CircularProgress />
          </div>
        ) : (
          <>
            <Title>{t("billing.title")}</Title>
            <div
              style={{
                display: "flex",
                marginTop: "2.06rem",
                gap: "2rem",
                marginBottom: "2.06rem",
              }}
            >
              <BillingSection>
                {isTrialPeriod ? (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "2rem",
                    }}
                  >
                    <Subheader>
                      {t("billing.plan.trialEnds", { date: trialExpiryDate })}
                    </Subheader>
                    <PlansDiv style={{ justifyContent: "center" }}>
                      <PricingCard
                        title={t("billing.plan.tiers.monthly")}
                        price="35"
                        bgColor="#51307F"
                        textColor="#ffffff"
                        buttonColor="#ffffff"
                        buttonTextColor="#51307F"
                        status={t("billing.plan.tiers.monthly_status") || "/month"}
                        feature1={t("billing.trialFeatures.monthly.feature1")}
                        feature2={t("billing.trialFeatures.monthly.feature2")}
                        feature3={t("billing.trialFeatures.monthly.feature3")}
                      />
                      <PricingCard
                        title={t("billing.plan.tiers.yearly")}
                        price="350"
                        bgColor="#ffffff"
                        textColor="#0F172A"
                        buttonColor="#51307F"
                        buttonTextColor="#ffffff"
                        status={t("billing.plan.tiers.yearly_status") || "/year"}
                        feature1={t("billing.trialFeatures.yearly.feature1")}
                        feature2={t("billing.trialFeatures.yearly.feature2")}
                        feature3={t("billing.trialFeatures.yearly.feature3")}
                      />
                    </PlansDiv>
                  </div>
                ) : (
                  <>
                    <Subheader>{t("billing.orderHistory.title")}</Subheader>
                    <Subheader
                      style={{
                        marginTop: "0.5rem",
                        color: "#696974",
                        fontSize: "0.875rem",
                        fontWeight: "500",
                      }}
                    >
                      {t("billing.orderHistory.subtitle")}
                    </Subheader>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        marginTop: "1.5rem",
                        overflowX: "auto",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          marginBottom: "1.25rem",
                          paddingLeft: "2rem",
                          paddingRight: "2rem",
                        }}
                      >
                        <Subheader style={{ flex: "2" }}>{t("billing.orderHistory.date")}</Subheader>
                        <Subheader style={{ flex: "3" }}>{t("billing.orderHistory.type")}</Subheader>
                        <Subheader>{t("billing.orderHistory.receipt")}</Subheader>
                      </div>
                      {invoices.map((invoice) => (
                        <RowDiv key={invoice.index}>
                          <RowText style={{ flex: "2" }}>
                            {new Date(invoice.createdDate).toLocaleDateString(
                              t("languageSelector.locale") || "en-US",
                              {
                                day: "2-digit",
                                month: "short",
                                year: "numeric",
                              },
                            )}
                          </RowText>
                          <RowText style={{ flex: "3" }}>
                            {invoice.details}
                          </RowText>
                          <DownloadButton
                            style={{ marginLeft: "auto" }}
                            onClick={() =>
                              handleDownloadInvoice(invoice.hostedInvoiceUrl)
                            }
                          >
                            <RowText style={{ lineHeight: "1rem" }}>
                              {t("billing.orderHistory.download")}
                            </RowText>
                          </DownloadButton>
                        </RowDiv>
                      ))}
                    </div>
                    <Subheader style={{ marginTop: "1.69rem" }}>
                      {t("billing.paymentMethod.title")}
                    </Subheader>
                    <div
                      style={{
                        display: "flex",
                        marginTop: "1.5rem",
                        alignItems: "center",
                        paddingRight: "1.25rem",
                      }}
                    >
                      <img
                        src={cardBrandImg}
                        alt="card"
                        style={{ width: "4.625rem", height: "2.8125rem" }}
                      />
                      <CardInfo style={{ marginLeft: "1.25rem" }}>
                        {t("billing.paymentMethod.endingIn", { brand: cardBrand, year: cardExpiryYear })}
                      </CardInfo>
                      <BillingButton
                        style={{ marginLeft: "auto" }}
                        onClick={() => handleshowCardModal()}
                      >
                        {t("billing.paymentMethod.change")}
                      </BillingButton>
                    </div>
                  </>
                )}
              </BillingSection>
              {!isTrialPeriod && (
                <div style={{ flex: "1" }}>
                  <SubscriptionCard>
                    <CardText>{t("billing.plan.title")}</CardText>
                    <CardText
                      style={{
                        fontSize: "1.5rem",
                        fontWeight: "600",
                        marginTop: "1.35rem",
                      }}
                    >
                      {t(`billing.planNames.${planName}`, planName)}
                    </CardText>
                    <SubText style={{ marginTop: "0.38rem" }}>
                      {isTrialPeriod
                        ? t("billing.plan.trialEnds", { date: trialExpiryDate })
                        : subsCancelsAt
                          ? t("billing.plan.cancelled", { date: periodEnd })
                          : t("billing.plan.renewsOn", { date: nextInvoiceDate })}
                    </SubText>
                    {isTrialPeriod ? (
                      <CancelButton
                        style={{ marginTop: "1.69rem", cursor: "pointer" }}
                        onClick={handleUpgradePlan}
                      >
                        {t("billing.plan.upgradePlan")}
                      </CancelButton>
                    ) : (
                      <>
                        {!subsCancelsAt && (
                          <CancelButton
                            style={{ marginTop: "1.69rem", cursor: "pointer" }}
                            onClick={handleCancelClick}
                            disabled={loading}
                          >
                            {loading ? (
                              <CircularProgress
                                size={24}
                                style={{ color: "#ffffff" }}
                              />
                            ) : (
                              t("billing.plan.cancelSub")
                            )}
                          </CancelButton>
                        )}

                        <BillingButton2
                          style={{ marginTop: "0.75rem", textColor: "white" }}
                          onClick={handleUpgradePlan}
                        >
                          {t("billing.plan.upgradeSub")}
                        </BillingButton2>

                        {currentPlanTier !== "essential" && (
                          <BillingButton2
                            style={{ marginTop: "0.75rem", background: "#FF6B6B", textColor: "white" }}
                            onClick={handleDowngradePlan}
                          >
                            {t("billing.plan.downgradeSub")}
                          </BillingButton2>
                        )}
                      </>
                    )}
                  </SubscriptionCard>
                </div>
              )}
            </div>
            <SnackbarAlert
              open={snackbarOpen}
              setOpen={setSnackbarOpen}
              message={snackbarMessage}
              severity={
                snackbarMessage === t("billing.messages.cancelSuccess") ||
                  snackbarMessage === t("billing.messages.upgradeSuccess")
                  ? "success"
                  : "error"
              }
            />
          </>
        )}
      </MainContainer>

      {/* Upgrade Cards Dialog */}
      <Dialog
        open={showUpgradeCards}
        onClose={handleCloseUpgradeCards}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {t("billing.plan.dialog.upgradeTitle")}
          <Button
            onClick={handleCloseUpgradeCards}
            style={{ position: "absolute", right: 8, top: 8 }}
          >
            ✕
          </Button>
        </DialogTitle>
        <DialogContent>
          {/* Toggle for Monthly/Yearly - hide for business monthly users */}
          {!(currentPlanTier === "business" && currentPlanMode === "monthly") && (
            <div style={{ display: "flex", justifyContent: "center", marginBottom: "1.5rem", alignItems: "center", gap: "1rem" }}>
              <span style={{ fontWeight: upgradeMode === "monthly" ? "600" : "400" }}>
                {t("billing.plan.monthly")}
              </span>
              <label style={{ position: "relative", display: "inline-block", width: "60px", height: "30px" }}>
                <input
                  type="checkbox"
                  checked={upgradeMode === "yearly"}
                  onChange={handleUpgradeModeToggle}
                  style={{ opacity: 0, width: 0, height: 0 }}
                />
                <span
                  style={{
                    position: "absolute",
                    cursor: "pointer",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: upgradeMode === "yearly" ? "#51307F" : "#ccc",
                    transition: "0.4s",
                    borderRadius: "30px",
                  }}
                >
                  <span
                    style={{
                      position: "absolute",
                      content: "",
                      height: "22px",
                      width: "22px",
                      left: upgradeMode === "yearly" ? "34px" : "4px",
                      bottom: "4px",
                      backgroundColor: "white",
                      transition: "0.4s",
                      borderRadius: "50%",
                    }}
                  />
                </span>
              </label>
              <span style={{ fontWeight: upgradeMode === "yearly" ? "600" : "400" }}>
                {t("billing.plan.yearly")}
              </span>
            </div>
          )}

          <PlansDiv style={{ justifyContent: "center", padding: "1rem 0" }}>
            {currentPlanTier === "essential" && (
              <>
                <PricingCard
                  title={t("payment.plans.advanced.title")}
                  price={upgradeMode === "monthly" ? "59" : "600"}
                  status={
                    upgradeMode === "monthly"
                      ? t("payment.plans.advanced.status")
                      : t("billing.plan.saveAmount", { amount: 59 * 12 - 600 })
                  }
                  planType="advanced"
                  mode={upgradeMode}
                  isUpgrade={true}
                  onUpgradeSuccess={handleUpgradeSuccess}
                  bgColor="#51307F"
                  textColor="#ffffff"
                  buttonColor="#ffffff"
                  buttonTextColor="#51307F"
                  feature1={t("payment.plans.advanced.feature1")}
                  feature2={t("payment.plans.advanced.feature2")}
                  feature3={t("payment.plans.advanced.feature3")}
                  feature4={t("payment.plans.advanced.feature4")}
                  feature5={t("payment.plans.advanced.feature5")}
                  feature6={t("payment.access")}
                />
                <PricingCard
                  title={t("payment.plans.business.title")}
                  price={upgradeMode === "monthly" ? "149" : "1520"}
                  status={
                    upgradeMode === "monthly"
                      ? t("payment.plans.advanced.status")
                      : t("billing.plan.saveAmount", { amount: 149 * 12 - 1520 })
                  }
                  planType="business"
                  mode={upgradeMode}
                  isUpgrade={true}
                  onUpgradeSuccess={handleUpgradeSuccess}
                  bgColor="#51307F"
                  textColor="#ffffff"
                  buttonColor="#ffffff"
                  buttonTextColor="#51307F"
                  feature1={t("payment.plans.business.feature1")}
                  feature2={t("payment.plans.business.feature2")}
                  feature3={t("payment.plans.business.feature3")}
                  feature4={t("payment.plans.business.feature4")}
                  feature5={t("payment.plans.business.feature5")}
                  feature6={t("payment.access")}
                  highlight={true}
                />
              </>
            )}
            {currentPlanTier === "advanced" && (
              <PricingCard
                title={t("payment.plans.business.title")}
                price={upgradeMode === "monthly" ? "149" : "1520"}
                status={
                  upgradeMode === "monthly"
                    ? t("payment.plans.advanced.status")
                    : t("billing.plan.saveAmount", { amount: 149 * 12 - 1520 })
                }
                planType="business"
                mode={upgradeMode}
                isUpgrade={true}
                onUpgradeSuccess={handleUpgradeSuccess}
                bgColor="#51307F"
                textColor="#ffffff"
                buttonColor="#ffffff"
                buttonTextColor="#51307F"
                feature1={t("payment.plans.business.feature1")}
                feature2={t("payment.plans.business.feature2")}
                feature3={t("payment.plans.business.feature3")}
                feature4={t("payment.plans.business.feature4")}
                feature5={t("payment.plans.business.feature5")}
                feature6={t("payment.access")}
                highlight={true}
              />
            )}
          </PlansDiv>
        </DialogContent>
      </Dialog>

      {/* Downgrade Cards Dialog */}
      <Dialog
        open={showDowngradeCards}
        onClose={handleCloseDowngradeCards}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {t("billing.plan.dialog.downgradeTitle")}
          <Button
            onClick={handleCloseDowngradeCards}
            style={{ position: "absolute", right: 8, top: 8 }}
          >
            ✕
          </Button>
        </DialogTitle>
        <DialogContent>
          <PlansDiv style={{ justifyContent: "center", padding: "1rem 0" }}>
            {currentPlanTier === "business" && (
              <>
                <PricingCard
                  title={t("payment.plans.advanced.title")}
                  price={currentPlanMode === "monthly" ? "59" : "600"}
                  status={
                    currentPlanMode === "monthly"
                      ? t("payment.plans.advanced.status")
                      : t("billing.plan.saveAmount", { amount: 59 * 12 - 600 })
                  }
                  planType="advanced"
                  mode={currentPlanMode}
                  isUpgrade={false}
                  isDowngrade={true}
                  onUpgradeSuccess={handleDowngradeSuccess}
                  bgColor="#51307F"
                  textColor="#ffffff"
                  buttonColor="#ffffff"
                  buttonTextColor="#51307F"
                  feature1={t("payment.plans.advanced.feature1")}
                  feature2={t("payment.plans.advanced.feature2")}
                  feature3={t("payment.plans.advanced.feature3")}
                  feature4={t("payment.plans.advanced.feature4")}
                  feature5={t("payment.plans.advanced.feature5")}
                  feature6={t("payment.access")}
                />
                <PricingCard
                  title={t("payment.plans.essential.title")}
                  price={currentPlanMode === "monthly" ? "35" : "350"}
                  status={
                    currentPlanMode === "monthly"
                      ? t("payment.plans.advanced.status")
                      : t("billing.plan.saveAmount", { amount: 35 * 12 - 350 })
                  }
                  planType="essential"
                  mode={currentPlanMode}
                  isUpgrade={false}
                  isDowngrade={true}
                  onUpgradeSuccess={handleDowngradeSuccess}
                  bgColor="#51307F"
                  textColor="#ffffff"
                  buttonColor="#ffffff"
                  buttonTextColor="#51307F"
                  feature1={t("payment.plans.essential.feature1")}
                  feature2={t("payment.plans.essential.feature2")}
                  feature3={t("payment.plans.essential.feature3")}
                  feature4={t("payment.plans.essential.feature4")}
                  feature5={t("payment.plans.essential.feature5")}
                  feature6={t("payment.access")}
                />
              </>
            )}
            {currentPlanTier === "advanced" && (
              <PricingCard
                title={t("payment.plans.essential.title")}
                price={currentPlanMode === "monthly" ? "35" : "350"}
                status={
                  currentPlanMode === "monthly"
                    ? t("payment.plans.advanced.status")
                    : t("billing.plan.saveAmount", { amount: 35 * 12 - 350 })
                }
                planType="essential"
                mode={currentPlanMode}
                isUpgrade={false}
                isDowngrade={true}
                onUpgradeSuccess={handleDowngradeSuccess}
                bgColor="#51307F"
                textColor="#ffffff"
                buttonColor="#ffffff"
                buttonTextColor="#51307F"
                feature1={t("payment.plans.essential.feature1")}
                feature2={t("payment.plans.essential.feature2")}
                feature3={t("payment.plans.essential.feature3")}
                feature4={t("payment.plans.essential.feature4")}
                feature5={t("payment.plans.essential.feature5")}
                feature6={t("payment.access")}
              />
            )}
          </PlansDiv>
        </DialogContent>
      </Dialog>

      {showCardModal && (
        <AddCardWrapper
          onClose={handleHideCardModal}
          card={card}
          onUpdate={fetchCardDetails}
        />
      )}
      <Dialog
        open={openConfirmDialog}
        onClose={handleCloseDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {t("billing.plan.dialog.cancelTitle")}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {t("billing.plan.dialog.cancelMessage")}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>{t("billing.plan.dialog.keepSub")}</Button>
          <Button onClick={handleConfirmCancel} autoFocus color="error">
            {t("billing.plan.dialog.confirmCancel")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Billing;
